import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import Navbar from '../components/Navbar';
import './StudentDetailPage.css';

function formatDate(dateValue) {
  if (!dateValue) return 'Unknown';
  const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function num(val) {
  if (val == null || val === '') return null;
  const n = Number(val);
  return isNaN(n) ? null : n;
}

function getPuttsForHole(hole) {
  if (num(hole.secondPuttDistance) === 0) return 1;
  if (hole.secondPuttResultRaw === 'Missed') return 3;
  if (hole.secondPuttResultRaw === 'Made It!') return 2;
  return 2;
}

function calcRoundStats(round) {
  const holes = round.holes || [];
  const holeCount = holes.length;
  const totalPutts = holes.reduce((sum, h) => sum + getPuttsForHole(h), 0);
  const threePutts = holes.filter((h) => h.secondPuttResultRaw === 'Missed').length;
  return { holeCount, totalPutts, threePutts };
}

// Miss direction: angle in degrees (0 = top/Long, clockwise)
const MISS_DIRECTIONS = {
  'Long':       { angle: 0,   color: '#2ECC71' },
  'Long Right': { angle: 45,  color: '#F39C12' },
  'Right':      { angle: 90,  color: '#3498DB' },
  'Short Right':{ angle: 135, color: '#E74C6F' },
  'Short':      { angle: 180, color: '#2ECC71' },
  'Short Left': { angle: 225, color: '#E74C6F' },
  'Left':       { angle: 270, color: '#9B59B6' },
  'Long Left':  { angle: 315, color: '#F39C12' },
};

const DIRECTION_ORDER = ['Long', 'Long Right', 'Right', 'Short Right', 'Short', 'Short Left', 'Left', 'Long Left'];

const DISTANCE_BUCKETS = [
  { label: '0–5 ft',  min: 0,  max: 5,  tourPct: 96 },
  { label: '6–8 ft',  min: 6,  max: 8,  tourPct: 72 },
  { label: '9–11 ft', min: 9,  max: 11, tourPct: 37 },
  { label: '12–15 ft', min: 12, max: 15, tourPct: 27 },
  { label: '16–19 ft', min: 16, max: 19, tourPct: 17 },
];

const MISS_CAUSE_LABELS = {
  1: 'Green Read',
  2: 'Line',
  3: 'Green Read + Line',
  4: 'Speed',
  5: 'Speed + Green Read',
  6: 'Speed + Line',
  7: 'Speed + Line + Green Read',
};

const MISS_CAUSE_COLORS = {
  'Speed + Green Read': '#E74C3C',
  'Green Read': '#C850C0',
  'Line + Green Read': '#00BCD4',
  'Green Read + Line': '#00BCD4',
  'Speed + Line + Green Read': '#E74C3C',
  'Speed': '#2ECC71',
  'Speed + Line': '#F39C12',
  'Line': '#3498DB',
};

const MISS_CAUSE_DESCRIPTIONS = {
  'Speed + Green Read': 'How your pace choice affected the break on these misses',
  'Green Read': 'The ball didn\'t break the way you expected',
  'Line + Green Read': 'How your start line and break read worked together on these misses',
  'Green Read + Line': 'How your start line and break read worked together on these misses',
  'Speed + Line + Green Read': 'Speed, line, and green read were all out of sync',
  'Speed': 'Speed was a factor in the miss — primarily about touch and tempo',
  'Speed + Line': 'Both speed and start line were factors in the miss',
  'Line': 'The ball started off your intended line',
};

const MISS_CAUSE_VALUES = {
  'Green Read': 1,
  'Line': 2,
  'Green Read + Line': 3,
  'Speed': 4,
  'Speed + Green Read': 5,
  'Speed + Line': 6,
  'Speed + Line + Green Read': 7,
};

// --- Physics-Based Miss Classifier ---
// Ported from StatsSharedPipeline.swift — player toggles take priority
function classifyMiss(hole) {
  if (!hole.secondPuttDistance || hole.secondPuttDistance === 0) {
    return { cause: 0, displayOwner: 'whyPuttsMiss' };
  }
  const missRaw = hole.missRaw;
  if (!missRaw || missRaw === '' || missRaw === 'Made It!') {
    return { cause: 0, displayOwner: 'whyPuttsMiss' };
  }
  const missLower = missRaw.toLowerCase();
  const isLeft = missLower.includes('left');
  const isRight = missLower.includes('right');
  const isShort = missLower.includes('short');
  const isLong = missLower.includes('long');
  const hasLateral = isLeft || isRight;
  const speedBad = hole.speedReadRaw && hole.speedReadRaw !== 'As Intended';
  const lineBad = hole.lineReadRaw && hole.lineReadRaw !== 'As Intended' && hole.lineReadRaw !== 'On Line';
  const breakDir = (hole.breakDirectionRaw || 'straight').toLowerCase();
  const isBreaking = breakDir === 'left' || breakDir === 'right';
  const isStraight = !isBreaking;

  let cause = 0;

  if (speedBad || lineBad) {
    // Player reported something — use only what they reported
    if (speedBad) cause |= 4;
    if (lineBad) cause |= 2;
    if (isBreaking && hasLateral) cause |= 1; // Green Read on breaking lateral miss
  } else {
    // Player said everything was fine — use physics baseline
    if (isStraight) {
      if (isShort && hasLateral) cause = 4;       // Speed
      else if (isLong && hasLateral) cause = 2;    // Line
      else if (hasLateral) cause = 2;              // Line
      else if (isShort) cause = 4;                 // Speed
      else if (isLong) cause = 2;                  // Line
      else cause = 6;                              // Speed + Line fallback
    } else {
      const pureShort = isShort && !hasLateral;
      const pureLong = isLong && !hasLateral;
      if (pureShort) {
        cause = 6; // Speed + Line
      } else if (pureLong) {
        cause = 5; // Speed + Green Read
      } else {
        let isHighSide = false, isLowSide = false;
        if (breakDir === 'left') { isHighSide = isRight; isLowSide = isLeft; }
        else if (breakDir === 'right') { isHighSide = isLeft; isLowSide = isRight; }
        if (isHighSide && !isShort && !isLong) cause = 1;       // Green Read
        else if (isLowSide && !isShort && !isLong) cause = 5;   // Speed + Green Read
        else if (isShort && isHighSide) cause = 3;               // Line + Green Read
        else if (isShort && isLowSide) cause = 7;                // Speed + Line + Green Read
        else if (isLong && isHighSide) cause = 5;                // Speed + Green Read
        else if (isLong && isLowSide) cause = 3;                 // Line + Green Read
        else cause = 6;                                          // Speed + Line fallback
      }
    }
  }

  let displayOwner = 'whyPuttsMiss';
  if (cause === 2 && isStraight && !lineBad) {
    displayOwner = 'straightPuttBias';
  }
  return { cause, displayOwner };
}

// Seeded random for consistent dot placement
function seededRandom(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function DidYouKnowCard({ item }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`dyk-card ${expanded ? 'dyk-expanded' : ''}`} onClick={() => setExpanded(!expanded)}>
      <div className="dyk-card-header">
        <span className="dyk-icon">{item.icon}</span>
        <div className="dyk-card-text">
          <span className="dyk-title">{item.title}</span>
          <span className="dyk-summary">{item.summary}</span>
        </div>
        <span className={`dyk-chevron ${expanded ? 'dyk-chevron-up' : ''}`}>&#9662;</span>
      </div>
      {expanded && (
        <div className="dyk-detail">
          {item.detail}
        </div>
      )}
    </div>
  );
}

function StudentDetailPage({ user }) {
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [coachData, setCoachData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCause, setSelectedCause] = useState(null);
  const [scatterBreakDir, setScatterBreakDir] = useState('right');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch coach data for navbar
        const coachSnap = await getDoc(doc(db, 'coaches', user.uid));
        if (coachSnap.exists()) {
          setCoachData(coachSnap.data());
        }

        // Fetch student document
        const studentSnap = await getDoc(doc(db, 'students', studentId));
        if (studentSnap.exists()) {
          setStudent({ id: studentSnap.id, ...studentSnap.data() });
        }

        // Fetch putting rounds for this student
        const q = query(
          collection(db, 'putting_rounds'),
          where('studentUID', '==', studentId)
        );
        const snapshot = await getDocs(q);
        const roundList = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        // Sort newest first by roundStartedAt
        roundList.sort((a, b) => {
          const dateA = a.roundStartedAt?.toDate ? a.roundStartedAt.toDate() : new Date(a.roundStartedAt || 0);
          const dateB = b.roundStartedAt?.toDate ? b.roundStartedAt.toDate() : new Date(b.roundStartedAt || 0);
          return dateB - dateA;
        });
        setRounds(roundList);
      } catch (err) {
        console.error('Error fetching student data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.uid, studentId]);

  // Flatten all holes across all rounds
  const allHoles = useMemo(() => rounds.flatMap((r) => r.holes || []), [rounds]);

  // Build array of individual putt attempts (first putts + second putts)
  const allPutts = useMemo(() => {
    const putts = [];
    allHoles.forEach((h) => {
      if (h.firstPuttDistance != null) {
        const made = !h.missRaw || h.missRaw === '' || h.missRaw === 'Made It!';
        putts.push({ distance: Number(h.firstPuttDistance), made, puttNumber: 1, hole: h });
      }
      if (h.secondPuttDistance != null && h.secondPuttDistance > 0) {
        const made = h.secondPuttResultRaw === 'Made It!';
        putts.push({ distance: Number(h.secondPuttDistance), made, puttNumber: 2, hole: h });
      }
    });
    return putts;
  }, [allHoles]);

  // Pre-classify all holes using physics-based classifier
  const classifiedHoles = useMemo(() => {
    return allHoles.map(h => ({ ...h, _classified: classifyMiss(h) }));
  }, [allHoles]);

  // --- Miss Direction: individual dots + breakdown ---
  const { missDots, missBreakdown, totalMisses, missPolygonPoints, missDirectionCounts } = useMemo(() => {
    const counts = {};
    const dots = [];
    let dotIndex = 0;

    allHoles.forEach((h) => {
      if (h.missRaw && MISS_DIRECTIONS[h.missRaw]) {
        const dir = h.missRaw;
        counts[dir] = (counts[dir] || 0) + 1;

        // Place dot with slight random offset
        const info = MISS_DIRECTIONS[dir];
        const angleRad = (info.angle - 90) * (Math.PI / 180);
        const baseRadius = 0.6;
        const offsetR = baseRadius + (seededRandom(dotIndex * 7 + 3) - 0.5) * 0.25;
        const offsetA = angleRad + (seededRandom(dotIndex * 13 + 7) - 0.5) * 0.35;
        dots.push({
          cx: 50 + offsetR * Math.cos(offsetA) * 38,
          cy: 50 + offsetR * Math.sin(offsetA) * 38,
          color: info.color,
        });
        dotIndex++;
      }
    });

    const total = Object.values(counts).reduce((s, c) => s + c, 0);
    const breakdown = DIRECTION_ORDER
      .filter((d) => counts[d])
      .map((d) => ({ label: d, count: counts[d], pct: Math.round((counts[d] / total) * 100), color: MISS_DIRECTIONS[d].color }))
      .sort((a, b) => b.count - a.count);

    // Build polygon points for connected shape
    const maxCount = Math.max(...DIRECTION_ORDER.map(d => counts[d] || 0), 1);
    const polygonPoints = DIRECTION_ORDER.map(d => {
      const count = counts[d] || 0;
      const ratio = count / maxCount;
      const radius = ratio * 34;
      const info = MISS_DIRECTIONS[d];
      const angleRad = (info.angle - 90) * (Math.PI / 180);
      const x = 50 + radius * Math.cos(angleRad);
      const y = 50 + radius * Math.sin(angleRad);
      return `${x},${y}`;
    }).join(' ');

    return { missDots: dots, missBreakdown: breakdown, totalMisses: total, missPolygonPoints: polygonPoints, missDirectionCounts: counts };
  }, [allHoles]);

  // --- Make % by Distance bar data (uses allPutts: first + second putts) ---
  const makeByDistanceData = useMemo(() => {
    return DISTANCE_BUCKETS.map((bucket) => {
      const inBucket = allPutts.filter((p) => p.distance >= bucket.min && p.distance <= bucket.max);
      const makes = inBucket.filter((p) => p.made).length;
      const pct = inBucket.length > 0 ? Math.round((makes / inBucket.length) * 100) : 0;
      return { label: bucket.label, pct, makes, total: inBucket.length, tourPct: bucket.tourPct };
    });
  }, [allPutts]);

  // --- Fix First: top 2 miss cause priorities with distance ranges ---
  const fixFirstData = useMemo(() => {
    const causeHoles = {};
    classifiedHoles.forEach((h) => {
      const val = h._classified.cause;
      if (val && val > 0 && MISS_CAUSE_LABELS[val] && h._classified.displayOwner === 'whyPuttsMiss') {
        const label = MISS_CAUSE_LABELS[val];
        if (!causeHoles[label]) causeHoles[label] = [];
        causeHoles[label].push(h);
      }
    });
    const totalMissed = Object.values(causeHoles).reduce((s, arr) => s + arr.length, 0);
    const sorted = Object.entries(causeHoles)
      .map(([label, holes]) => {
        // Find the distance range where this happens most
        const distBuckets = { '0–5 ft': 0, '6–19 ft': 0, '20+ ft': 0 };
        holes.forEach((h) => {
          const d = num(h.firstPuttDistance);
          if (d == null) return;
          if (d <= 5) distBuckets['0–5 ft']++;
          else if (d <= 19) distBuckets['6–19 ft']++;
          else distBuckets['20+ ft']++;
        });
        const topRange = Object.entries(distBuckets).sort((a, b) => b[1] - a[1])[0];
        return {
          label,
          count: holes.length,
          pct: totalMissed > 0 ? Math.round((holes.length / totalMissed) * 100) : 0,
          distanceRange: topRange[1] > 0 ? topRange[0] : 'All distances',
        };
      })
      .sort((a, b) => b.count - a.count);
    return sorted.slice(0, 2);
  }, [classifiedHoles]);

  // --- Lag Putting: 20+ ft analysis ---
  const lagData = useMemo(() => {
    const lagBuckets = [
      { label: '20–29 ft', min: 20, max: 29, goal: 90 },
      { label: '30–39 ft', min: 30, max: 39, goal: 85 },
      { label: '40+ ft',   min: 40, max: Infinity, goal: 80 },
    ];
    const missedLag = allHoles.filter((h) => {
      const d = num(h.firstPuttDistance);
      return d != null && d >= 20 && h.missRaw && h.missRaw !== '' && h.missRaw !== 'Made It!';
    });

    const proximityRows = lagBuckets.map((b) => {
      const inBucket = missedLag.filter((h) => { const d = num(h.firstPuttDistance); return d != null && d >= b.min && d <= b.max; });
      const within4 = inBucket.filter((h) => { const sd = num(h.secondPuttDistance); return sd != null && sd <= 4; }).length;
      const pct = inBucket.length > 0 ? Math.round((within4 / inBucket.length) * 100) : null;
      return { label: b.label, pct, goal: b.goal, count: inBucket.length, meetsGoal: pct !== null && pct >= b.goal };
    });

    const avgLeaveRows = lagBuckets.map((b) => {
      const inBucket = missedLag.filter((h) => { const d = num(h.firstPuttDistance); return d != null && d >= b.min && d <= b.max; });
      const withDist = inBucket.filter((h) => num(h.secondPuttDistance) != null);
      const avg = withDist.length > 0
        ? (withDist.reduce((s, h) => s + num(h.secondPuttDistance), 0) / withDist.length).toFixed(1)
        : null;
      return { label: b.label, avg, count: inBucket.length };
    });

    // Speed diagnosis
    const perceptionMiss = missedLag.filter((h) => h.speedReadRaw === 'As Intended').length;
    const skillMiss = missedLag.filter((h) => h.speedReadRaw && h.speedReadRaw !== 'As Intended').length;
    const diagTotal = perceptionMiss + skillMiss;

    return {
      proximityRows,
      avgLeaveRows,
      perceptionMiss,
      skillMiss,
      diagTotal,
      totalLagMisses: missedLag.length,
    };
  }, [allHoles]);

  // --- Straight Putt Bias ---
  const straightBiasData = useMemo(() => {
    const straightMisses = classifiedHoles.filter((h) =>
      h.breakDirectionRaw === 'straight' &&
      h.missRaw && h.missRaw !== '' && h.missRaw !== 'Made It!'
    );
    const leftDirs = ['Left', 'Short Left', 'Long Left'];
    const rightDirs = ['Right', 'Short Right', 'Long Right'];
    const leftCount = straightMisses.filter((h) => leftDirs.includes(h.missRaw)).length;
    const rightCount = straightMisses.filter((h) => rightDirs.includes(h.missRaw)).length;
    const total = leftCount + rightCount;
    const leftPct = total > 0 ? Math.round((leftCount / total) * 100) : 0;
    const rightPct = total > 0 ? Math.round((rightCount / total) * 100) : 0;
    const dominant = leftPct >= 65 ? 'left' : rightPct >= 65 ? 'right' : null;
    return { leftCount, rightCount, leftPct, rightPct, total, totalMisses: straightMisses.length, dominant };
  }, [classifiedHoles]);

  // --- Miss Cause Combinations card data ---
  const { missCauseData, missCauseTotal } = useMemo(() => {
    const counts = {};
    let total = 0;
    classifiedHoles.forEach((h) => {
      const val = h._classified.cause;
      if (val && val > 0 && MISS_CAUSE_LABELS[val] && h._classified.displayOwner === 'whyPuttsMiss') {
        const label = MISS_CAUSE_LABELS[val];
        counts[label] = (counts[label] || 0) + 1;
        total++;
      }
    });
    const data = Object.entries(counts)
      .map(([label, count]) => ({
        label,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0,
        color: MISS_CAUSE_COLORS[label] || '#2ECC71',
        description: MISS_CAUSE_DESCRIPTIONS[label] || '',
      }))
      .sort((a, b) => b.count - a.count);
    return { missCauseData: data, missCauseTotal: total };
  }, [classifiedHoles]);

  // --- Miss Analysis drill-down for selected cause ---
  const missAnalysisData = useMemo(() => {
    if (!selectedCause) return null;

    const causeValue = MISS_CAUSE_VALUES[selectedCause];
    if (!causeValue) return null;

    const causeHoles = classifiedHoles.filter(h => h._classified.cause === causeValue && h._classified.displayOwner === 'whyPuttsMiss');
    if (causeHoles.length === 0) return null;

    const hasSpeed = (causeValue & 4) !== 0;
    const hasLine = (causeValue & 2) !== 0;
    const hasGreenRead = (causeValue & 1) !== 0;

    // --- SINGLE PILLAR: Speed (causeValue=4) ---
    if (causeValue === 4) {
      const longDirs = ['Long', 'Long Left', 'Long Right'];
      const shortDirs = ['Short', 'Short Left', 'Short Right'];
      const speedRelatedHoles = classifiedHoles.filter(h => { const v = h._classified.cause; return v && (v & 4) !== 0 && h._classified.displayOwner === 'whyPuttsMiss'; });
      const srLong = speedRelatedHoles.filter(h => longDirs.includes(h.missRaw)).length;
      const srShort = speedRelatedHoles.filter(h => shortDirs.includes(h.missRaw)).length;
      const srTotal = srLong + srShort;
      const srLongPct = srTotal > 0 ? Math.round((srLong / srTotal) * 100) : 0;
      const srShortPct = srTotal > 0 ? Math.round((srShort / srTotal) * 100) : 0;

      let coaching = '';
      if (srLongPct >= 65) {
        coaching = "Hitting it past the hole more often than leaving it short. This can mean tension is creeping into the stroke \u2014 the body adds speed when the mind tightens. Focus on staying relaxed through impact.";
      } else if (srShortPct >= 65) {
        coaching = "Leaving putts short consistently. This often comes from deceleration through impact or being tentative with pace. Commit to the stroke and let the putter release through the ball.";
      } else {
        coaching = "Speed misses are balanced between long and short. The issue is consistency of pace control rather than a directional tendency. Focus on developing a repeatable tempo.";
      }

      return {
        type: 'speed',
        label: selectedCause,
        description: MISS_CAUSE_DESCRIPTIONS[selectedCause],
        exactCount: causeHoles.length,
        relatedCount: speedRelatedHoles.length,
        longCount: srLong,
        shortCount: srShort,
        longPct: srLongPct,
        shortPct: srShortPct,
        coaching,
      };
    }

    // --- SINGLE PILLAR: Line (causeValue=2) ---
    if (causeValue === 2) {
      const lineRelatedHoles = classifiedHoles.filter(h => { const v = h._classified.cause; return v && (v & 2) !== 0 && h._classified.displayOwner === 'whyPuttsMiss'; });
      const leftOfIntended = lineRelatedHoles.filter(h => h.lineReadRaw === 'Left of Intended').length;
      const rightOfIntended = lineRelatedHoles.filter(h => h.lineReadRaw === 'Right of Intended').length;
      const lineTotal = leftOfIntended + rightOfIntended;
      const leftPct = lineTotal > 0 ? Math.round((leftOfIntended / lineTotal) * 100) : 0;
      const rightPct = lineTotal > 0 ? Math.round((rightOfIntended / lineTotal) * 100) : 0;

      let coaching = '';
      if (leftPct >= 65) {
        coaching = "Start line is consistently pulling left. This could be an alignment issue at setup or the putter face closing through impact. Check that shoulders and feet are square to the intended start line.";
      } else if (rightPct >= 65) {
        coaching = "Start line is consistently pushing right. This could be an alignment issue at setup or the putter face opening through impact. Check that shoulders and feet are square to the intended start line.";
      } else {
        coaching = "Start line misses are balanced between left and right. The issue is consistency of face control rather than a directional tendency. Focus on a stable setup and repeatable stroke path.";
      }

      return {
        type: 'line',
        label: selectedCause,
        description: MISS_CAUSE_DESCRIPTIONS[selectedCause],
        exactCount: causeHoles.length,
        relatedCount: lineRelatedHoles.length,
        leftOfIntended,
        rightOfIntended,
        leftPct,
        rightPct,
        coaching,
      };
    }

    // --- SINGLE PILLAR: Green Read (causeValue=1) ---
    if (causeValue === 1) {
      const grRelatedHoles = classifiedHoles.filter(h => { const v = h._classified.cause; return v && (v & 1) !== 0 && h._classified.displayOwner === 'whyPuttsMiss'; });
      const dirCounts = {};
      grRelatedHoles.forEach(h => {
        if (h.missRaw && MISS_DIRECTIONS[h.missRaw]) {
          dirCounts[h.missRaw] = (dirCounts[h.missRaw] || 0) + 1;
        }
      });
      const dirTotal = Object.values(dirCounts).reduce((s, c) => s + c, 0);
      const dirBreakdown = DIRECTION_ORDER
        .filter(d => dirCounts[d])
        .map(d => ({
          label: d,
          count: dirCounts[d],
          pct: dirTotal > 0 ? Math.round((dirCounts[d] / dirTotal) * 100) : 0,
          color: MISS_DIRECTIONS[d].color,
        }))
        .sort((a, b) => b.count - a.count);

      const dots = [];
      let dotIdx = 0;
      grRelatedHoles.forEach(h => {
        if (h.missRaw && MISS_DIRECTIONS[h.missRaw]) {
          const info = MISS_DIRECTIONS[h.missRaw];
          const angleRad = (info.angle - 90) * (Math.PI / 180);
          const baseRadius = 0.6;
          const offsetR = baseRadius + (seededRandom(dotIdx * 7 + 3) - 0.5) * 0.25;
          const offsetA = angleRad + (seededRandom(dotIdx * 13 + 7) - 0.5) * 0.35;
          dots.push({
            cx: 50 + offsetR * Math.cos(offsetA) * 38,
            cy: 50 + offsetR * Math.sin(offsetA) * 38,
            color: '#C850C0',
          });
          dotIdx++;
        }
      });

      let coaching = '';
      if (dirBreakdown.length > 0) {
        const topDir = dirBreakdown[0];
        coaching = `The most common green read miss direction is ${topDir.label} (${topDir.pct}%). The ball isn't breaking the way it's expected to. This could be a true misread of the green, or speed may have changed how much the ball broke.`;
      }

      // Build polygon for Green Read radar
      const grMaxCount = Math.max(...DIRECTION_ORDER.map(d => dirCounts[d] || 0), 1);
      const grPolygonPoints = DIRECTION_ORDER.map(d => {
        const count = dirCounts[d] || 0;
        const ratio = count / grMaxCount;
        const radius = ratio * 34;
        const info = MISS_DIRECTIONS[d];
        const angleRad = (info.angle - 90) * (Math.PI / 180);
        const x = 50 + radius * Math.cos(angleRad);
        const y = 50 + radius * Math.sin(angleRad);
        return `${x},${y}`;
      }).join(' ');

      return {
        type: 'greenRead',
        label: selectedCause,
        description: MISS_CAUSE_DESCRIPTIONS[selectedCause],
        exactCount: causeHoles.length,
        relatedCount: grRelatedHoles.length,
        dirBreakdown,
        dirCounts,
        dots,
        polygonPoints: grPolygonPoints,
        coaching,
      };
    }

    // --- COMBINATION CAUSES (3, 5, 6, 7) ---
    const rightBreaking = causeHoles.filter(h => h.breakDirectionRaw === 'right');
    const leftBreaking = causeHoles.filter(h => h.breakDirectionRaw === 'left');
    const straightPutts = causeHoles.filter(h => h.breakDirectionRaw === 'straight');

    const buildScatterDots = (holes, breakDir) => {
      const longDirs = ['Long', 'Long Left', 'Long Right'];
      const shortDirs = ['Short', 'Short Left', 'Short Right'];
      const leftDirs = ['Left', 'Short Left', 'Long Left'];
      const rightDirs = ['Right', 'Short Right', 'Long Right'];

      return holes.map((h, idx) => {
        if (!h.missRaw || !MISS_DIRECTIONS[h.missRaw]) return null;

        let y = 50;
        if (longDirs.includes(h.missRaw)) y = 15 + seededRandom(idx * 11 + 5) * 20;
        else if (shortDirs.includes(h.missRaw)) y = 65 + seededRandom(idx * 11 + 5) * 20;

        let x = 50;
        if (breakDir === 'right') {
          if (leftDirs.includes(h.missRaw)) x = 15 + seededRandom(idx * 17 + 3) * 20;
          else if (rightDirs.includes(h.missRaw)) x = 65 + seededRandom(idx * 17 + 3) * 20;
        } else if (breakDir === 'left') {
          // Left-breaking: miss right = high side → RIGHT of chart (flipped)
          if (rightDirs.includes(h.missRaw)) x = 65 + seededRandom(idx * 17 + 3) * 20;
          else if (leftDirs.includes(h.missRaw)) x = 15 + seededRandom(idx * 17 + 3) * 20;
        }

        x += (seededRandom(idx * 23 + 7) - 0.5) * 8;
        y += (seededRandom(idx * 29 + 11) - 0.5) * 8;

        let isReported = false;
        if (hasSpeed && h.speedReadRaw && h.speedReadRaw !== 'As Intended') isReported = true;
        if (hasLine && h.lineReadRaw && h.lineReadRaw !== 'As Intended') isReported = true;

        return { x, y, isReported, lineDirection: h.lineReadRaw || null, missDirection: h.missRaw || null };
      }).filter(Boolean);
    };

    const rightDots = buildScatterDots(rightBreaking, 'right');
    const leftDots = buildScatterDots(leftBreaking, 'left');

    const buildPatternDistribution = (holes, breakDir) => {
      const longDirs = ['Long', 'Long Left', 'Long Right'];
      const shortDirs = ['Short', 'Short Left', 'Short Right'];
      const leftDirs = ['Left', 'Short Left', 'Long Left'];
      const rightDirs = ['Right', 'Short Right', 'Long Right'];

      const patterns = {};
      let total = 0;

      holes.forEach(h => {
        if (!h.missRaw || !MISS_DIRECTIONS[h.missRaw]) return;

        const isLong = longDirs.includes(h.missRaw);
        const isShort = shortDirs.includes(h.missRaw);
        const isLeft = leftDirs.includes(h.missRaw);
        const isRight = rightDirs.includes(h.missRaw);
        const hasLateral = isLeft || isRight;

        let isHighSide = false;
        let isLowSide = false;
        if (breakDir === 'right') {
          isHighSide = isLeft;
          isLowSide = isRight;
        } else if (breakDir === 'left') {
          isHighSide = isRight;
          isLowSide = isLeft;
        }

        let pattern = '';
        if (isLong && isHighSide) pattern = 'Long + High-side';
        else if (isLong && isLowSide) pattern = 'Long + Low-side';
        else if (isShort && isHighSide) pattern = 'Short + High-side';
        else if (isShort && isLowSide) pattern = 'Short + Low-side';
        else if (isLong && !hasLateral) pattern = 'Pure Long';
        else if (isShort && !hasLateral) pattern = 'Pure Short';
        else if (isHighSide && !isLong && !isShort) pattern = 'Pure High-side';
        else if (isLowSide && !isLong && !isShort) pattern = 'Pure Low-side';
        else pattern = 'Other';

        if (pattern && pattern !== 'Other') {
          patterns[pattern] = (patterns[pattern] || 0) + 1;
          total++;
        }
      });

      return Object.entries(patterns)
        .map(([label, count]) => ({
          label,
          count,
          pct: total > 0 ? Math.round((count / total) * 100) : 0,
        }))
        .sort((a, b) => b.count - a.count);
    };

    let coaching = '';
    if (causeValue === 5) {
      const speedReported = causeHoles.filter(h => h.speedReadRaw && h.speedReadRaw !== 'As Intended').length;
      const speedInferred = causeHoles.length - speedReported;
      if (speedInferred > speedReported) {
        coaching = `On most of these misses, speed felt fine but the miss pattern suggests pace affected the break. The ball may be rolling at a speed that changes how much it breaks \u2014 hitting it firmer reduces break, softer increases it. ${rightBreaking.length > leftBreaking.length ? 'This shows up more on right-breakers.' : leftBreaking.length > rightBreaking.length ? 'This shows up more on left-breakers.' : 'It shows up on both break directions.'}`;
      } else {
        coaching = `Speed was a recognized factor on these misses \u2014 pace choice is directly affecting how the ball takes the break. When speed is off, the read becomes wrong even if the green was read correctly. ${rightBreaking.length > leftBreaking.length ? 'Right-breaking putts are more affected.' : leftBreaking.length > rightBreaking.length ? 'Left-breaking putts are more affected.' : 'Both break directions are affected equally.'}`;
      }
    } else if (causeValue === 3) {
      const lineReported = causeHoles.filter(h => h.lineReadRaw && h.lineReadRaw !== 'As Intended').length;
      const lineInferred = causeHoles.length - lineReported;
      if (lineInferred > lineReported) {
        coaching = 'Start line felt on target but the miss pattern suggests it drifted. When start line is slightly off on a breaking putt, it compounds the read error \u2014 the ball starts on the wrong line AND breaks differently than expected.';
      } else {
        coaching = 'Both start line and green read are off on these putts. The start line is going where it\'s not intended, and the break read isn\'t matching the green. Focus on getting the start line right first \u2014 a good line with a slightly wrong read still has a chance.';
      }
    } else if (causeValue === 6) {
      coaching = 'Both speed and start line are factors on these misses. This combination often shows up under pressure \u2014 tension affects both pace and face angle simultaneously. Focus on a smooth, committed stroke to address both at once.';
    } else if (causeValue === 7) {
      coaching = 'All three pillars were factors on these misses \u2014 speed, line, and green read were all out of sync. These putts represent the highest difficulty. With more rounds tracked, focus on the most frequent single-pillar issue first.';
    }

    return {
      type: 'combination',
      label: selectedCause,
      description: MISS_CAUSE_DESCRIPTIONS[selectedCause],
      totalCount: causeHoles.length,
      rightBreaking: { count: rightBreaking.length, dots: rightDots },
      leftBreaking: { count: leftBreaking.length, dots: leftDots },
      rightPatterns: buildPatternDistribution(rightBreaking, 'right'),
      leftPatterns: buildPatternDistribution(leftBreaking, 'left'),
      straightCount: straightPutts.length,
      hasSpeed,
      hasLine,
      hasGreenRead,
      coaching,
    };
  }, [selectedCause, classifiedHoles, missCauseData]);

  // --- Hero Card data (uses allPutts for make %, allHoles for 3-putt rate) ---
  const heroData = useMemo(() => {
    const madeCount = allPutts.filter(p => p.made).length;
    const overallMakePct = allPutts.length > 0 ? Math.round((madeCount / allPutts.length) * 100) : 0;

    const shortPutts = allPutts.filter(p => p.distance <= 5);
    const shortMade = shortPutts.filter(p => p.made).length;
    const shortMakePct = shortPutts.length > 0 ? Math.round((shortMade / shortPutts.length) * 100) : null;

    const threePuttHoles = allHoles.filter(h => h.secondPuttResultRaw === 'Missed').length;
    const threePuttRate = allHoles.length > 0 ? Math.round((threePuttHoles / allHoles.length) * 100) : 0;

    return { overallMakePct, shortMakePct, shortMade, shortTotal: shortPutts.length, threePuttRate, threePuttHoles };
  }, [allPutts, allHoles]);

  // --- Top Insights ---
  const topInsights = useMemo(() => {
    const insights = [];

    // 1. Putts made 5ft & under (uses allPutts)
    const shortPutts = allPutts.filter(p => p.distance <= 5);
    const shortMade = shortPutts.filter(p => p.made).length;
    const shortPct = shortPutts.length > 0 ? Math.round((shortMade / shortPutts.length) * 100) : null;
    if (shortPutts.length > 0) {
      insights.push({
        icon: '\u25CE',
        label: 'Putts made 5 ft & under',
        value: `${shortPct}% (${shortMade}/${shortPutts.length})`,
        color: '#2ECC71',
      });
    }

    // 2. Why Putts Miss (Most Common)
    if (missCauseData.length > 0) {
      insights.push({
        icon: '\u258A',
        label: 'Why Putts Miss (Most Common)',
        value: missCauseData[0].label,
        color: missCauseData[0].color || '#2ECC71',
      });
    }

    // 3. Straight Putt Bias
    if (straightBiasData.total > 0) {
      insights.push({
        icon: '\u2194',
        label: 'Straight Putt Bias',
        value: `${straightBiasData.leftPct}% left, ${straightBiasData.rightPct}% right`,
        color: '#3498DB',
      });
    }

    // 4. Lag Putting — average leave from 20+ ft
    const lagMisses = allHoles.filter(h => {
      const d = num(h.firstPuttDistance);
      return d != null && d >= 20 && h.missRaw && h.missRaw !== '' && h.missRaw !== 'Made It!' && num(h.secondPuttDistance) != null;
    });
    if (lagMisses.length > 0) {
      const avgLeave = (lagMisses.reduce((s, h) => s + num(h.secondPuttDistance), 0) / lagMisses.length).toFixed(1);
      insights.push({
        icon: '\u25A6',
        label: 'Lag Putting',
        value: `${avgLeave} ft avg leave (\u226520 ft)`,
        color: '#2ECC71',
      });
    }

    // 5. Miss Direction (Most Common)
    if (missBreakdown.length > 0) {
      insights.push({
        icon: '\u2198',
        label: 'Miss Direction (Most Common)',
        value: `${missBreakdown[0].label} (${missBreakdown[0].pct}%)`,
        color: missBreakdown[0].color || '#2ECC71',
      });
    }

    return insights;
  }, [allHoles, allPutts, missCauseData, straightBiasData, missBreakdown]);

  // --- Did You Know? insights ---
  const didYouKnowInsights = useMemo(() => {
    const insights = [];

    // 1. Break Direction Bias
    const leftBreakers = allHoles.filter(h => h.breakDirectionRaw === 'left');
    const rightBreakers = allHoles.filter(h => h.breakDirectionRaw === 'right');
    const leftMade = leftBreakers.filter(h => !h.missRaw || h.missRaw === '' || h.missRaw === 'Made It!').length;
    const rightMade = rightBreakers.filter(h => !h.missRaw || h.missRaw === '' || h.missRaw === 'Made It!').length;
    const leftMakePct = leftBreakers.length >= 3 ? Math.round((leftMade / leftBreakers.length) * 100) : null;
    const rightMakePct = rightBreakers.length >= 3 ? Math.round((rightMade / rightBreakers.length) * 100) : null;

    if (leftMakePct !== null && rightMakePct !== null) {
      const diff = Math.abs(leftMakePct - rightMakePct);
      if (diff >= 10) {
        const better = leftMakePct > rightMakePct ? 'left' : 'right';
        const worse = better === 'left' ? 'right' : 'left';
        const betterPct = better === 'left' ? leftMakePct : rightMakePct;
        const worsePct = better === 'left' ? rightMakePct : leftMakePct;
        insights.push({
          icon: 'Y',
          title: 'Break Direction Bias',
          summary: `Reads ${better}-breakers better than ${worse}-breakers \u2014 ${diff} point gap`,
          detail: `On ${better}-breaking putts, make rate is ${betterPct}% (${better === 'left' ? leftMade : rightMade}/${better === 'left' ? leftBreakers.length : rightBreakers.length}). On ${worse}-breaking putts, it drops to ${worsePct}% (${worse === 'left' ? leftMade : rightMade}/${worse === 'left' ? leftBreakers.length : rightBreakers.length}). This ${diff >= 20 ? 'significant' : 'moderate'} gap suggests the green read is more challenging on ${worse}-breakers.`,
        });
      }
    }

    // 2. Distance Read Bias
    const breakingMisses = allHoles.filter(h =>
      h.breakDirectionRaw && h.breakDirectionRaw !== 'straight' &&
      h.missRaw && h.missRaw !== '' && h.missRaw !== 'Made It!' &&
      num(h.firstPuttDistance) != null
    );
    const shortBreaking = breakingMisses.filter(h => num(h.firstPuttDistance) <= 12);
    const longBreaking = breakingMisses.filter(h => num(h.firstPuttDistance) > 12);

    const getHighLowSide = (holes) => {
      let high = 0, low = 0;
      holes.forEach(h => {
        const missDir = h.missRaw;
        const breakDir = h.breakDirectionRaw;
        const rightMissDirs = ['Right', 'Short Right', 'Long Right'];
        const leftMissDirs = ['Left', 'Short Left', 'Long Left'];
        if (breakDir === 'left' && rightMissDirs.includes(missDir)) high++;
        else if (breakDir === 'left' && leftMissDirs.includes(missDir)) low++;
        else if (breakDir === 'right' && leftMissDirs.includes(missDir)) high++;
        else if (breakDir === 'right' && rightMissDirs.includes(missDir)) low++;
      });
      return { high, low };
    };

    if (shortBreaking.length >= 3 && longBreaking.length >= 3) {
      const shortHL = getHighLowSide(shortBreaking);
      const longHL = getHighLowSide(longBreaking);
      const shortHighPct = (shortHL.high + shortHL.low) > 0 ? Math.round((shortHL.high / (shortHL.high + shortHL.low)) * 100) : null;
      const longHighPct = (longHL.high + longHL.low) > 0 ? Math.round((longHL.high / (longHL.high + longHL.low)) * 100) : null;

      if (shortHighPct !== null && longHighPct !== null) {
        let summaryText = '';
        if (longHighPct >= 55 && shortHighPct < 45) {
          summaryText = 'Reads long breakers well, but inside 12 feet misses high-side more often';
        } else if (shortHighPct >= 55 && longHighPct < 45) {
          summaryText = 'Reads short-range break well, but outside 12 feet tends to miss low-side';
        } else if (shortHighPct >= 55 && longHighPct >= 55) {
          summaryText = 'Tends to under-read break at all distances \u2014 missing high-side consistently';
        } else if (shortHighPct < 45 && longHighPct < 45) {
          summaryText = 'Tends to over-read break at all distances \u2014 missing low-side consistently';
        } else {
          summaryText = 'No strong high/low-side pattern \u2014 miss distribution is fairly balanced';
        }

        insights.push({
          icon: '\u25A6',
          title: 'Distance Read Bias',
          summary: summaryText,
          detail: `Inside 12 ft: ${shortHL.high} high-side, ${shortHL.low} low-side (${shortHighPct}% high). Outside 12 ft: ${longHL.high} high-side, ${longHL.low} low-side (${longHighPct}% high). ${shortHighPct >= 60 || longHighPct >= 60 ? 'The high-side tendency suggests under-reading break.' : shortHighPct <= 40 || longHighPct <= 40 ? 'The low-side tendency suggests over-reading break.' : 'The mix is relatively balanced.'}`,
        });
      }
    }

    // 3. Short Putt Bias
    const shortMisses = allHoles.filter(h => {
      const d = num(h.firstPuttDistance);
      return d != null && d <= 6 && h.missRaw && h.missRaw !== '' && h.missRaw !== 'Made It!';
    });
    if (shortMisses.length >= 3) {
      const leftDirs = ['Left', 'Short Left', 'Long Left'];
      const rightDirs = ['Right', 'Short Right', 'Long Right'];
      const leftCount = shortMisses.filter(h => leftDirs.includes(h.missRaw)).length;
      const rightCount = shortMisses.filter(h => rightDirs.includes(h.missRaw)).length;
      const sideTotal = leftCount + rightCount;
      if (sideTotal >= 3) {
        const leftPct = Math.round((leftCount / sideTotal) * 100);
        const rightPct = Math.round((rightCount / sideTotal) * 100);
        const dominant = leftPct >= 60 ? 'left' : rightPct >= 60 ? 'right' : null;
        if (dominant) {
          const domPct = dominant === 'left' ? leftPct : rightPct;
          const domCount = dominant === 'left' ? leftCount : rightCount;
          insights.push({
            icon: '\u25CE',
            title: 'Short Putt Bias',
            summary: `Inside 6 feet, ${domPct}% of misses go ${dominant}`,
            detail: `On short putts (6 ft and under), ${domCount} of ${sideTotal} directional misses went ${dominant} (${domPct}%). On short putts this often points to setup or putter face alignment at address rather than stroke mechanics.`,
          });
        }
      }
    }

    // 4. Speed Control Pattern
    const speedTagged = allHoles.filter(h => h.speedReadRaw);
    const asIntended = speedTagged.filter(h => h.speedReadRaw === 'As Intended');
    const notAsIntended = speedTagged.filter(h => h.speedReadRaw !== 'As Intended');
    const aiMade = asIntended.filter(h => !h.missRaw || h.missRaw === '' || h.missRaw === 'Made It!').length;
    const naiMade = notAsIntended.filter(h => !h.missRaw || h.missRaw === '' || h.missRaw === 'Made It!').length;

    if (asIntended.length >= 5 && notAsIntended.length >= 5) {
      const aiPct = Math.round((aiMade / asIntended.length) * 100);
      const naiPct = Math.round((naiMade / notAsIntended.length) * 100);
      const diff = aiPct - naiPct;
      if (Math.abs(diff) >= 10) {
        insights.push({
          icon: '\u26A1',
          title: 'Speed Control Impact',
          summary: diff > 0
            ? `Make rate jumps ${diff} points when speed is as intended`
            : 'Surprisingly, make rate is similar even when speed is off \u2014 the miss cause may be elsewhere',
          detail: `When speed is "As Intended": ${aiPct}% make rate (${aiMade}/${asIntended.length}). When speed is off: ${naiPct}% make rate (${naiMade}/${notAsIntended.length}). ${diff > 0 ? `The ${diff}-point gap shows speed control is a meaningful lever for improvement.` : 'The similarity suggests other factors (line, read) are the bigger issue.'}`,
        });
      }
    }

    // 5. Line Control Pattern
    const lineTagged = allHoles.filter(h => h.lineReadRaw);
    const lineAsIntended = lineTagged.filter(h => h.lineReadRaw === 'As Intended');
    const lineNotAsIntended = lineTagged.filter(h => h.lineReadRaw !== 'As Intended');
    const laiMade = lineAsIntended.filter(h => !h.missRaw || h.missRaw === '' || h.missRaw === 'Made It!').length;
    const lnaiMade = lineNotAsIntended.filter(h => !h.missRaw || h.missRaw === '' || h.missRaw === 'Made It!').length;

    if (lineAsIntended.length >= 5 && lineNotAsIntended.length >= 5) {
      const laiPct = Math.round((laiMade / lineAsIntended.length) * 100);
      const lnaiPct = Math.round((lnaiMade / lineNotAsIntended.length) * 100);
      const diff = laiPct - lnaiPct;
      if (Math.abs(diff) >= 10) {
        insights.push({
          icon: '\u279E',
          title: 'Start Line Impact',
          summary: diff > 0
            ? `Make rate jumps ${diff} points when start line is on target`
            : 'Make rate holds up even when start line drifts \u2014 read or speed may matter more',
          detail: `When line is "As Intended": ${laiPct}% make rate (${laiMade}/${lineAsIntended.length}). When line is off: ${lnaiPct}% make rate (${lnaiMade}/${lineNotAsIntended.length}). ${diff > 0 ? `The ${diff}-point gap shows start line is a key factor.` : 'This suggests line is less of a differentiator than expected.'}`,
        });
      }
    }

    return insights;
  }, [allHoles, missCauseData, straightBiasData, missBreakdown]);

  // Set default selected cause when data loads
  useEffect(() => {
    if (missCauseData.length > 0 && selectedCause === null) {
      setSelectedCause(missCauseData[0].label);
    }
  }, [missCauseData, selectedCause]);

  // Reset scatter break direction when selected cause changes
  useEffect(() => {
    if (missAnalysisData && missAnalysisData.type === 'combination') {
      if (missAnalysisData.rightBreaking.count >= missAnalysisData.leftBreaking.count) {
        setScatterBreakDir('right');
      } else {
        setScatterBreakDir('left');
      }
    }
  }, [selectedCause]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Compute summary stats from holes arrays
  const totalRounds = rounds.length;
  const allStats = rounds.map(calcRoundStats);
  const totalPutts = allStats.reduce((sum, s) => sum + s.totalPutts, 0);
  const totalHoles = allStats.reduce((sum, s) => sum + s.holeCount, 0);
  const puttsPerRound = totalHoles > 0 ? ((totalPutts / totalHoles) * 18).toFixed(1) : '—';
  const threePuttCount = allStats.reduce((sum, s) => sum + s.threePutts, 0);

  return (
    <div className="dashboard">
      <Navbar coachName={coachData?.brandName || coachData?.name || 'Coach'} />

      <div className="dashboard-content">
        <Link to="/dashboard" className="back-link">← Back to Dashboard</Link>

        <div className="student-header">
          <h1>{student?.name || 'Unknown Student'}</h1>
          {student?.connectedAt && (
            <p className="student-since">Connected {formatDate(student.connectedAt || student.createdAt)}</p>
          )}
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{totalRounds}</div>
            <div className="stat-label">Total Rounds</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{totalPutts}</div>
            <div className="stat-label">Total Putts</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{puttsPerRound}</div>
            <div className="stat-label">Putts / Round</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{threePuttCount}</div>
            <div className="stat-label">Three-Putts</div>
          </div>
        </div>

        {/* Hero Card */}
        {allHoles.length > 0 && (
          <div className="hero-card">
            <div className="hero-rings">
              {/* Ring 1: Overall Make % */}
              <div className="hero-ring-item">
                <div className="hero-ring">
                  <svg viewBox="0 0 120 120" className="hero-ring-svg">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
                    <circle
                      cx="60" cy="60" r="52"
                      fill="none"
                      stroke="#2ECC71"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${heroData.overallMakePct * 3.267} ${326.7 - heroData.overallMakePct * 3.267}`}
                      strokeDashoffset="81.675"
                      style={{ transition: 'stroke-dasharray 0.6s ease' }}
                    />
                  </svg>
                  <div className="hero-ring-inner">
                    <span className="hero-ring-value">{heroData.overallMakePct}%</span>
                    <span className="hero-ring-label">MAKE %</span>
                  </div>
                </div>
                <span className="hero-ring-caption">Overall</span>
              </div>

              {/* Ring 2: Short Putt Make % (5ft & under) */}
              <div className="hero-ring-item">
                <div className="hero-ring">
                  <svg viewBox="0 0 120 120" className="hero-ring-svg">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
                    <circle
                      cx="60" cy="60" r="52"
                      fill="none"
                      stroke="#3498DB"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${(heroData.shortMakePct || 0) * 3.267} ${326.7 - (heroData.shortMakePct || 0) * 3.267}`}
                      strokeDashoffset="81.675"
                      style={{ transition: 'stroke-dasharray 0.6s ease' }}
                    />
                  </svg>
                  <div className="hero-ring-inner">
                    <span className="hero-ring-value">{heroData.shortMakePct !== null ? `${heroData.shortMakePct}%` : '\u2014'}</span>
                    <span className="hero-ring-label">MAKE %</span>
                  </div>
                </div>
                <span className="hero-ring-caption">5 ft & under</span>
              </div>

              {/* Ring 3: 3-Putt Rate */}
              <div className="hero-ring-item">
                <div className="hero-ring">
                  <svg viewBox="0 0 120 120" className="hero-ring-svg">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
                    <circle
                      cx="60" cy="60" r="52"
                      fill="none"
                      stroke={heroData.threePuttRate > 10 ? '#E74C3C' : '#F39C12'}
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${heroData.threePuttRate * 3.267} ${326.7 - heroData.threePuttRate * 3.267}`}
                      strokeDashoffset="81.675"
                      style={{ transition: 'stroke-dasharray 0.6s ease' }}
                    />
                  </svg>
                  <div className="hero-ring-inner">
                    <span className="hero-ring-value">{heroData.threePuttRate}%</span>
                    <span className="hero-ring-label">3-PUTT</span>
                  </div>
                </div>
                <span className="hero-ring-caption">~{heroData.threePuttHoles} per {totalRounds > 0 ? Math.round(totalHoles / totalRounds) : 18} holes</span>
              </div>
            </div>

            {/* Fix First callout */}
            {fixFirstData.length > 0 && (
              <div className="hero-fix-callout">
                <span className="hero-fix-icon">&#9888;</span>
                <div className="hero-fix-text">
                  <span className="hero-fix-label">Fix First</span>
                  <span className="hero-fix-value">{fixFirstData[0].label} — {fixFirstData[0].distanceRange}</span>
                </div>
              </div>
            )}

            <div className="hero-meta">
              {totalRounds} round{totalRounds !== 1 ? 's' : ''} &middot; {totalPutts} putts tracked
            </div>
          </div>
        )}

        {/* Top Insights */}
        {topInsights.length > 0 && (
          <div className="chart-section" style={{ marginTop: '24px' }}>
            <h2>Top Insights</h2>
            <div className="insights-list">
              {topInsights.map((item, i) => (
                <div key={i} className="insight-card">
                  <div className="insight-icon-circle" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                    <span className="insight-icon">{item.icon}</span>
                  </div>
                  <div className="insight-text">
                    <span className="insight-label">{item.label}</span>
                    <span className="insight-value">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {allHoles.length > 0 && (
          <>
            {/* Fix First */}
            {fixFirstData.length > 0 && (
              <div className="chart-section">
                <h2>Fix First</h2>
                <div className="fix-first-cards">
                  {fixFirstData.map((item, i) => (
                    <div key={item.label} className={`fix-first-card ${i === 0 ? 'fix-top' : 'fix-next'}`}>
                      <div className="fix-card-header">
                        <div className="fix-icon-wrap">
                          {i === 0 ? (
                            <span className="fix-icon-triangle">&#9888;</span>
                          ) : (
                            <span className="fix-icon-circle">&#9679;</span>
                          )}
                        </div>
                        <div className="fix-card-info">
                          <span className="fix-card-title">{item.label}</span>
                          <span className="fix-card-range">Most common at {item.distanceRange}</span>
                        </div>
                        <span className={`fix-badge ${i === 0 ? 'fix-badge-top' : 'fix-badge-next'}`}>
                          {i === 0 ? 'Top Priority' : 'Next Priority'}
                        </span>
                      </div>
                      <div className="fix-card-stat">
                        {item.count} misses ({item.pct}%)
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Why Putts Miss — Side by Side */}
            <div className="chart-section">
              <h2>Why Putts Miss</h2>
              <div className="wpm-layout">
                {/* Left: Combination cards */}
                <div className="wpm-left">
                  {missCauseData.map((item) => (
                    <div
                      key={item.label}
                      className={`cause-card ${selectedCause === item.label ? 'cause-card-selected' : ''}`}
                      onClick={() => setSelectedCause(item.label)}
                    >
                      <div className="cause-card-header">
                        <div className="cause-card-left">
                          <span className="cause-dot" style={{ backgroundColor: item.color }}></span>
                          <span className="cause-title">{item.label}</span>
                        </div>
                        <span className="cause-count">{item.count}</span>
                      </div>
                      <p className="cause-desc">{item.description}</p>
                      <div className="cause-bar-track">
                        <div
                          className="cause-bar-fill"
                          style={{ width: `${item.pct}%`, backgroundColor: item.color }}
                        >
                          {item.pct >= 12 && <span className="cause-bar-label">{item.pct}%</span>}
                        </div>
                        {item.pct < 12 && <span className="cause-bar-label-outside" style={{ color: item.color }}>{item.pct}%</span>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right: Analysis panel */}
                <div className="wpm-right">
                  {missAnalysisData && (
                    <div className="analysis-panel">
                      <div className="analysis-header">
                        <span className="analysis-dot" style={{ backgroundColor: MISS_CAUSE_COLORS[selectedCause] || '#2ECC71' }}></span>
                        <div>
                          <h3 className="analysis-title">{selectedCause}</h3>
                          <p className="analysis-desc">{MISS_CAUSE_DESCRIPTIONS[selectedCause]}</p>
                        </div>
                      </div>

                      {/* === COMBINATION CAUSE: Scatter chart === */}
                      {missAnalysisData.type === 'combination' && (
                        <>
                          <div className="analysis-count">
                            {missAnalysisData.totalCount} putts where {selectedCause.toLowerCase().replace(/ \+ /g, ' and ')} {missAnalysisData.totalCount === 1 ? 'was' : 'were'} a factor
                          </div>

                          <div className="scatter-tabs">
                            <button
                              className={`scatter-tab ${scatterBreakDir === 'right' ? 'scatter-tab-active' : ''}`}
                              onClick={() => setScatterBreakDir('right')}
                            >
                              Right-Breaking ({missAnalysisData.rightBreaking.count})
                            </button>
                            <button
                              className={`scatter-tab ${scatterBreakDir === 'left' ? 'scatter-tab-active' : ''}`}
                              onClick={() => setScatterBreakDir('left')}
                            >
                              Left-Breaking ({missAnalysisData.leftBreaking.count})
                            </button>
                          </div>

                          <div className="analysis-scatter-container">
                            <svg viewBox="0 0 100 100" className="analysis-scatter-svg">
                              <rect x="5" y="5" width="90" height="90" rx="4" fill="rgba(255,255,255,0.03)" />
                              <line x1="50" y1="8" x2="50" y2="92" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
                              <line x1="8" y1="50" x2="92" y2="50" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
                              <circle cx="50" cy="50" r="3" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
                              <circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.3)" />
                              <circle cx="50" cy="50" r="30" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.3" />
                              <text x="50" y="5" textAnchor="middle" fill="#999" fontSize="3" fontWeight="600">Long</text>
                              <text x="50" y="98" textAnchor="middle" fill="#999" fontSize="3" fontWeight="600">Short</text>
                              <text x="6" y="51" textAnchor="start" fill="#999" fontSize="2.6" fontWeight="600">{scatterBreakDir === 'right' ? 'High' : 'Low'}</text>
                              <text x="94" y="51" textAnchor="end" fill="#999" fontSize="2.6" fontWeight="600">{scatterBreakDir === 'right' ? 'Low' : 'High'}</text>
                              {(scatterBreakDir === 'right' ? missAnalysisData.rightBreaking.dots : missAnalysisData.leftBreaking.dots).map((dot, i) => {
                                if (selectedCause === 'Green Read + Line') {
                                  // Determine direction: use reported lineReadRaw first, then infer from missRaw
                                  let pointsLeft = dot.lineDirection === 'Left of Intended';
                                  let pointsRight = dot.lineDirection === 'Right of Intended';
                                  if (!pointsLeft && !pointsRight && dot.missDirection) {
                                    const leftMisses = ['Left', 'Short Left', 'Long Left'];
                                    const rightMisses = ['Right', 'Short Right', 'Long Right'];
                                    if (leftMisses.includes(dot.missDirection)) pointsLeft = true;
                                    else if (rightMisses.includes(dot.missDirection)) pointsRight = true;
                                  }
                                  const size = 3;
                                  let trianglePoints;
                                  if (pointsLeft) {
                                    trianglePoints = `${dot.x - size},${dot.y} ${dot.x + size},${dot.y - size} ${dot.x + size},${dot.y + size}`;
                                  } else if (pointsRight) {
                                    trianglePoints = `${dot.x + size},${dot.y} ${dot.x - size},${dot.y - size} ${dot.x - size},${dot.y + size}`;
                                  } else {
                                    return dot.isReported ? (
                                      <circle key={i} cx={dot.x} cy={dot.y} r="2" fill="#2ECC71" fillOpacity="0.9" />
                                    ) : (
                                      <circle key={i} cx={dot.x} cy={dot.y} r="2" fill="none" stroke="#2ECC71" strokeWidth="0.6" opacity="0.9" />
                                    );
                                  }
                                  return dot.isReported ? (
                                    <polygon key={i} points={trianglePoints} fill="#2ECC71" fillOpacity="0.9" />
                                  ) : (
                                    <polygon key={i} points={trianglePoints} fill="none" stroke="#2ECC71" strokeWidth="0.6" opacity="0.9" />
                                  );
                                }
                                return dot.isReported ? (
                                  <circle key={i} cx={dot.x} cy={dot.y} r="2" fill="#2ECC71" fillOpacity="0.9" />
                                ) : (
                                  <circle key={i} cx={dot.x} cy={dot.y} r="2" fill="none" stroke="#2ECC71" strokeWidth="0.6" opacity="0.9" />
                                );
                              })}
                            </svg>
                            <div className="scatter-axis-subtitle">
                              {scatterBreakDir === 'right' ? '← High-side' : '← Low-side'}
                              &nbsp;&nbsp;|&nbsp;&nbsp;
                              {scatterBreakDir === 'right' ? 'Low-side →' : 'High-side →'}
                            </div>
                          </div>

                          {(missAnalysisData.hasSpeed || missAnalysisData.hasLine) && (
                            <div className="scatter-legend">
                              {selectedCause === 'Green Read + Line' ? (
                                <>
                                  <div className="scatter-legend-item">
                                    <svg width="14" height="14" viewBox="0 0 14 14"><polygon points="10,7 4,3 4,11" fill="#2ECC71" /></svg>
                                    <span>Reported</span>
                                  </div>
                                  <div className="scatter-legend-item">
                                    <svg width="14" height="14" viewBox="0 0 14 14"><polygon points="10,7 4,3 4,11" fill="none" stroke="#2ECC71" strokeWidth="1.5" /></svg>
                                    <span>Inferred</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="scatter-legend-item">
                                    <span className="scatter-legend-dot-solid"></span>
                                    <span>Reported</span>
                                  </div>
                                  <div className="scatter-legend-item">
                                    <span className="scatter-legend-dot-hollow"></span>
                                    <span>Inferred</span>
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          {(missAnalysisData.hasSpeed || missAnalysisData.hasLine) && (
                            <p className="scatter-legend-explain">
                              {selectedCause === 'Green Read + Line'
                                ? 'Solid triangles are putts where a line issue was reported. Hollow triangles are inferred. Triangles point in the direction the ball started \u2014 left or right of intended line.'
                                : `Solid dots are putts where ${missAnalysisData.hasSpeed && missAnalysisData.hasLine ? 'speed or line issues were' : missAnalysisData.hasSpeed ? 'a speed issue was' : 'a line issue was'} reported. Hollow dots are where the miss pattern suggests ${missAnalysisData.hasSpeed && missAnalysisData.hasLine ? 'speed or line were factors' : missAnalysisData.hasSpeed ? 'speed was a factor' : 'line was a factor'}, even though execution felt fine.`
                              }
                            </p>
                          )}

                          {/* Pattern Distribution */}
                          {(() => {
                            const patterns = scatterBreakDir === 'right'
                              ? missAnalysisData.rightPatterns
                              : missAnalysisData.leftPatterns;
                            return patterns && patterns.length > 0 ? (
                              <div className="pattern-dist">
                                <h4 className="pattern-dist-title">Pattern Distribution</h4>
                                {patterns.map((p, i) => (
                                  <div key={p.label} className="pattern-dist-row">
                                    <span className="pattern-dist-dot" style={{
                                      backgroundColor: i === 0 ? '#E74C3C' : i === 1 ? '#F39C12' : '#2ECC71'
                                    }}></span>
                                    <span className="pattern-dist-label">{p.label}</span>
                                    <span className="pattern-dist-pct">{p.pct}%</span>
                                    <span className="pattern-dist-count">({p.count})</span>
                                  </div>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </>
                      )}

                      {/* === SPEED: Long vs Short bars === */}
                      {missAnalysisData.type === 'speed' && (
                        <>
                          <div className="analysis-count">
                            {missAnalysisData.relatedCount} putts where speed was a factor
                            {missAnalysisData.relatedCount !== missAnalysisData.exactCount && (
                              <span className="analysis-count-sub">(Includes combinations with other factors)</span>
                            )}
                          </div>

                          <div className="speed-bars">
                            <div className="speed-bar-row">
                              <span className="speed-bar-icon">&uarr;</span>
                              <span className="speed-bar-label">Long</span>
                              <div className="speed-bar-track">
                                <div className="speed-bar-fill speed-bar-long" style={{ width: `${missAnalysisData.longPct}%` }}></div>
                              </div>
                              <span className="speed-bar-pct">{missAnalysisData.longPct}%</span>
                              <span className="speed-bar-count">({missAnalysisData.longCount})</span>
                            </div>
                            <div className="speed-bar-row">
                              <span className="speed-bar-icon">&darr;</span>
                              <span className="speed-bar-label">Short</span>
                              <div className="speed-bar-track">
                                <div className="speed-bar-fill speed-bar-short" style={{ width: `${missAnalysisData.shortPct}%` }}></div>
                              </div>
                              <span className="speed-bar-pct">{missAnalysisData.shortPct}%</span>
                              <span className="speed-bar-count">({missAnalysisData.shortCount})</span>
                            </div>
                          </div>
                        </>
                      )}

                      {/* === LINE: Left vs Right bars === */}
                      {missAnalysisData.type === 'line' && (
                        <>
                          <div className="analysis-count">
                            {missAnalysisData.relatedCount} putts where line was a factor
                            {missAnalysisData.relatedCount !== missAnalysisData.exactCount && (
                              <span className="analysis-count-sub">(Includes combinations with other factors)</span>
                            )}
                          </div>

                          <div className="speed-bars">
                            <div className="speed-bar-row">
                              <span className="speed-bar-icon">&larr;</span>
                              <span className="speed-bar-label">Left of Intended</span>
                              <div className="speed-bar-track">
                                <div className="speed-bar-fill speed-bar-line-left" style={{ width: `${missAnalysisData.leftPct}%` }}></div>
                              </div>
                              <span className="speed-bar-pct">{missAnalysisData.leftPct}%</span>
                              <span className="speed-bar-count">({missAnalysisData.leftOfIntended})</span>
                            </div>
                            <div className="speed-bar-row">
                              <span className="speed-bar-icon">&rarr;</span>
                              <span className="speed-bar-label">Right of Intended</span>
                              <div className="speed-bar-track">
                                <div className="speed-bar-fill speed-bar-line-right" style={{ width: `${missAnalysisData.rightPct}%` }}></div>
                              </div>
                              <span className="speed-bar-pct">{missAnalysisData.rightPct}%</span>
                              <span className="speed-bar-count">({missAnalysisData.rightOfIntended})</span>
                            </div>
                          </div>
                        </>
                      )}

                      {/* === GREEN READ: Radar scatter + breakdown === */}
                      {missAnalysisData.type === 'greenRead' && (
                        <>
                          <div className="analysis-count">
                            {missAnalysisData.relatedCount} putts where green reading was a factor
                            {missAnalysisData.relatedCount !== missAnalysisData.exactCount && (
                              <span className="analysis-count-sub">(Includes combinations with other factors)</span>
                            )}
                          </div>

                          <div className="gr-radar-container">
                            <svg viewBox="0 0 100 100" className="gr-radar-svg">
                              <circle cx="50" cy="50" r="38" fill="rgba(0,0,0,0.2)" />
                              <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
                              <circle cx="50" cy="50" r="26" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />
                              <circle cx="50" cy="50" r="14" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="0.5" />
                              <line x1="50" y1="10" x2="50" y2="90" stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" />
                              <line x1="10" y1="50" x2="90" y2="50" stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" />
                              <line x1="22" y1="22" x2="78" y2="78" stroke="rgba(255,255,255,0.10)" strokeWidth="0.3" />
                              <line x1="78" y1="22" x2="22" y2="78" stroke="rgba(255,255,255,0.10)" strokeWidth="0.3" />
                              <circle cx="50" cy="50" r="2.5" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
                              <text x="50" y="6" textAnchor="middle" fill="#999" fontSize="3.2" fontWeight="600">Long</text>
                              <text x="50" y="97" textAnchor="middle" fill="#999" fontSize="3.2" fontWeight="600">Short</text>
                              <text x="4" y="51" textAnchor="middle" fill="#999" fontSize="3.2" fontWeight="600">Left</text>
                              <text x="96" y="51" textAnchor="middle" fill="#999" fontSize="3.2" fontWeight="600">Right</text>
                              <text x="81" y="16" textAnchor="middle" fill="#666" fontSize="2.5">Long Right</text>
                              <text x="19" y="16" textAnchor="middle" fill="#666" fontSize="2.5">Long Left</text>
                              <text x="81" y="88" textAnchor="middle" fill="#666" fontSize="2.5">Short Right</text>
                              <text x="19" y="88" textAnchor="middle" fill="#666" fontSize="2.5">Short Left</text>
                              {missAnalysisData.polygonPoints && (
                                <>
                                  <polygon
                                    points={missAnalysisData.polygonPoints}
                                    fill="rgba(200, 80, 200, 0.15)"
                                    stroke="rgba(200, 80, 200, 0.6)"
                                    strokeWidth="0.8"
                                    strokeLinejoin="round"
                                  />
                                  {DIRECTION_ORDER.map(d => {
                                    const count = (missAnalysisData.dirCounts && missAnalysisData.dirCounts[d]) || 0;
                                    if (count === 0) return null;
                                    const maxC = Math.max(...DIRECTION_ORDER.map(dir => (missAnalysisData.dirCounts[dir] || 0)), 1);
                                    const ratio = count / maxC;
                                    const radius = ratio * 34;
                                    const info = MISS_DIRECTIONS[d];
                                    const angleRad = (info.angle - 90) * (Math.PI / 180);
                                    const x = 50 + radius * Math.cos(angleRad);
                                    const y = 50 + radius * Math.sin(angleRad);
                                    return <circle key={d} cx={x} cy={y} r="1.5" fill="rgba(200, 80, 200, 0.9)" />;
                                  })}
                                </>
                              )}
                            </svg>
                          </div>

                          {missAnalysisData.dirBreakdown.length > 0 && (
                            <div className="gr-breakdown">
                              {missAnalysisData.dirBreakdown.map((item) => (
                                <div key={item.label} className="gr-breakdown-row">
                                  <span className="gr-breakdown-arrow" style={{ color: item.color }}>&#9654;</span>
                                  <span className="gr-breakdown-label">{item.label}</span>
                                  <div className="gr-breakdown-track">
                                    <div className="gr-breakdown-fill" style={{ width: `${item.pct}%`, backgroundColor: item.color }} />
                                  </div>
                                  <span className="gr-breakdown-pct">{item.pct}%</span>
                                  <span className="gr-breakdown-count">({item.count})</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}

                      {/* Coaching message — all types */}
                      {missAnalysisData.coaching && (
                        <div className="analysis-coaching">
                          <span className="analysis-coaching-icon">💡</span>
                          <p className="analysis-coaching-text">{missAnalysisData.coaching}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Did You Know? */}
            {didYouKnowInsights.length > 0 && (
              <div className="chart-section">
                <h2>Did You Know?</h2>
                <div className="dyk-cards">
                  {didYouKnowInsights.map((item, i) => (
                    <DidYouKnowCard key={i} item={item} />
                  ))}
                </div>
              </div>
            )}

            {/* Make % by Distance — Table */}
            <div className="chart-section">
              <h2>Make % by Distance</h2>
              <div className="chart-card make-table-card">
                <table className="make-table">
                  <thead>
                    <tr>
                      <th className="make-th-left">Distance</th>
                      <th>Make %</th>
                      <th>Tour</th>
                      <th className="make-th-right">Makes / Attempts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {makeByDistanceData.map((row) => (
                      <tr key={row.label}>
                        <td className="make-td-distance">{row.label}</td>
                        <td className="make-td-pct">{row.total > 0 ? `${row.pct}%` : '—'}</td>
                        <td className="make-td-tour">{row.tourPct}%</td>
                        <td className="make-td-ratio">{row.total > 0 ? `${row.makes} / ${row.total}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Lag Putting */}
            {lagData.totalLagMisses > 0 && (
              <div className="chart-section">
                <h2>Lag Putting</h2>

                <div className="chart-card lag-card">
                  <h3 className="lag-subtitle">% of putts finish 4ft or less</h3>
                  <table className="lag-table">
                    <thead>
                      <tr>
                        <th className="lag-th-left">Distance</th>
                        <th>Percentage</th>
                        <th>Goal</th>
                        <th className="lag-th-right">Putts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lagData.proximityRows.map((row) => (
                        <tr key={row.label}>
                          <td className="lag-td-distance">{row.label}</td>
                          <td className={`lag-td-pct ${row.pct !== null ? (row.meetsGoal ? 'lag-green' : 'lag-red') : ''}`}>
                            {row.pct !== null ? `${row.pct}%` : '—'}
                          </td>
                          <td className="lag-td-goal">{row.goal}%</td>
                          <td className="lag-td-count">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="chart-card lag-card" style={{ marginTop: '12px' }}>
                  <h3 className="lag-subtitle">Avg Leave</h3>
                  <table className="lag-table">
                    <thead>
                      <tr>
                        <th className="lag-th-left">Distance</th>
                        <th>Avg Leave</th>
                        <th className="lag-th-right">Misses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lagData.avgLeaveRows.map((row) => (
                        <tr key={row.label}>
                          <td className="lag-td-distance">{row.label}</td>
                          <td className="lag-td-pct lag-green">
                            {row.avg !== null ? `${row.avg} ft` : '—'}
                          </td>
                          <td className="lag-td-count">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {lagData.diagTotal > 0 && (
                  <div className="chart-card lag-card" style={{ marginTop: '12px' }}>
                    <h3 className="lag-subtitle">Speed Diagnosis</h3>
                    <div className="speed-diag">
                      <div className="speed-diag-row">
                        <div className="speed-diag-info">
                          <span className="speed-diag-title">Perception Miss</span>
                          <span className="speed-diag-desc">Hit intended speed but read was off</span>
                        </div>
                        <div className="speed-diag-stats">
                          <span className="speed-diag-pct lag-green">
                            {lagData.diagTotal > 0 ? Math.round((lagData.perceptionMiss / lagData.diagTotal) * 100) : 0}%
                          </span>
                          <span className="speed-diag-count">({lagData.perceptionMiss})</span>
                        </div>
                      </div>
                      <div className="speed-diag-row">
                        <div className="speed-diag-info">
                          <span className="speed-diag-title">Skill Miss</span>
                          <span className="speed-diag-desc">Didn't hit intended speed</span>
                        </div>
                        <div className="speed-diag-stats">
                          <span className="speed-diag-pct lag-red">
                            {lagData.diagTotal > 0 ? Math.round((lagData.skillMiss / lagData.diagTotal) * 100) : 0}%
                          </span>
                          <span className="speed-diag-count">({lagData.skillMiss})</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Straight Putt Bias */}
            {straightBiasData.total > 0 && (
              <div className="chart-section">
                <h2>Straight Putt Bias</h2>
                <div className="chart-card">
                  <p className="bias-subtitle">On straight putts, which side do you tend to miss?</p>

                  <div className="bias-bars-container">
                    <div className="bias-bar-col">
                      <div className="bias-bar-wrapper">
                        <div
                          className="bias-bar bias-bar-left"
                          style={{ height: `${straightBiasData.total > 0 ? Math.max((straightBiasData.leftCount / Math.max(straightBiasData.leftCount, straightBiasData.rightCount)) * 100, 12) : 0}%` }}
                        >
                          <span className="bias-bar-value">{straightBiasData.leftCount}</span>
                        </div>
                      </div>
                      <span className="bias-bar-label">Left-bias</span>
                      <span className="bias-bar-pct">{straightBiasData.leftPct}%</span>
                    </div>
                    <div className="bias-bar-col">
                      <div className="bias-bar-wrapper">
                        <div
                          className="bias-bar bias-bar-right"
                          style={{ height: `${straightBiasData.total > 0 ? Math.max((straightBiasData.rightCount / Math.max(straightBiasData.leftCount, straightBiasData.rightCount)) * 100, 12) : 0}%` }}
                        >
                          <span className="bias-bar-value">{straightBiasData.rightCount}</span>
                        </div>
                      </div>
                      <span className="bias-bar-label">Right-bias</span>
                      <span className="bias-bar-pct">{straightBiasData.rightPct}%</span>
                    </div>
                  </div>

                  {straightBiasData.dominant && (
                    <div className="bias-insight">
                      <span className="bias-insight-icon">&#9888;</span>
                      <span className="bias-insight-text">
                        Strong {straightBiasData.dominant}-bias on straight putts. Check your setup and putter face alignment.
                      </span>
                    </div>
                  )}

                  <div className="bias-total">
                    {straightBiasData.totalMisses} straight putt{straightBiasData.totalMisses !== 1 ? 's' : ''} missed
                  </div>
                </div>
              </div>
            )}

            {/* Miss Direction — Radar-style */}
            <div className="chart-section">
              <h2>Miss Direction</h2>
              <div className="chart-card">
                <div className="radar-container">
                  <svg viewBox="0 0 100 100" className="radar-svg">
                    <circle cx="50" cy="50" r="38" fill="rgba(0,0,0,0.2)" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
                    <circle cx="50" cy="50" r="26" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />
                    <circle cx="50" cy="50" r="14" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="0.5" />
                    <line x1="50" y1="10" x2="50" y2="90" stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" />
                    <line x1="10" y1="50" x2="90" y2="50" stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" />
                    <line x1="22" y1="22" x2="78" y2="78" stroke="rgba(255,255,255,0.10)" strokeWidth="0.3" />
                    <line x1="78" y1="22" x2="22" y2="78" stroke="rgba(255,255,255,0.10)" strokeWidth="0.3" />
                    <circle cx="50" cy="50" r="2.5" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
                    <text x="50" y="6" textAnchor="middle" fill="#999" fontSize="3.2" fontWeight="600">LONG</text>
                    <text x="50" y="97" textAnchor="middle" fill="#999" fontSize="3.2" fontWeight="600">SHORT</text>
                    <text x="4" y="51" textAnchor="middle" fill="#999" fontSize="3.2" fontWeight="600">LEFT</text>
                    <text x="96" y="51" textAnchor="middle" fill="#999" fontSize="3.2" fontWeight="600">RIGHT</text>
                    <text x="81" y="16" textAnchor="middle" fill="#666" fontSize="2.5">Long Right</text>
                    <text x="19" y="16" textAnchor="middle" fill="#666" fontSize="2.5">Long Left</text>
                    <text x="81" y="88" textAnchor="middle" fill="#666" fontSize="2.5">Short Right</text>
                    <text x="19" y="88" textAnchor="middle" fill="#666" fontSize="2.5">Short Left</text>
                    {totalMisses > 0 && (
                      <>
                        <polygon
                          points={missPolygonPoints}
                          fill="rgba(200, 80, 200, 0.15)"
                          stroke="rgba(200, 80, 200, 0.6)"
                          strokeWidth="0.8"
                          strokeLinejoin="round"
                        />
                        {DIRECTION_ORDER.map(d => {
                          const count = missDirectionCounts[d] || 0;
                          if (count === 0) return null;
                          const ratio = count / Math.max(...DIRECTION_ORDER.map(dir => missDirectionCounts[dir] || 0), 1);
                          const radius = ratio * 34;
                          const info = MISS_DIRECTIONS[d];
                          const angleRad = (info.angle - 90) * (Math.PI / 180);
                          const x = 50 + radius * Math.cos(angleRad);
                          const y = 50 + radius * Math.sin(angleRad);
                          return <circle key={d} cx={x} cy={y} r="1.5" fill="rgba(200, 80, 200, 0.9)" />;
                        })}
                      </>
                    )}
                  </svg>
                </div>

                {missBreakdown.length > 0 && (
                  <div className="miss-breakdown">
                    {missBreakdown.map((item) => (
                      <div key={item.label} className="miss-breakdown-row">
                        <span className="miss-arrow" style={{ color: item.color }}>&#9654;</span>
                        <span className="miss-dir-label">{item.label}</span>
                        <div className="miss-bar-track">
                          <div
                            className="miss-bar-fill"
                            style={{ width: `${item.pct}%`, backgroundColor: item.color }}
                          />
                        </div>
                        <span className="miss-pct">{item.pct}%</span>
                        <span className="miss-count">({item.count})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Round History — at the bottom */}
        <div className="rounds-section">
          <h2>Round History</h2>
          {rounds.length === 0 ? (
            <div className="empty-rounds">
              <p>No rounds recorded yet.</p>
            </div>
          ) : (
            <div className="rounds-list">
              <div className="rounds-header">
                <span>Date</span>
                <span>Holes</span>
                <span>Total Putts</span>
                <span>Putts / Hole</span>
              </div>
              {rounds.map((round) => {
                const stats = calcRoundStats(round);
                const avg = stats.holeCount > 0 ? (stats.totalPutts / stats.holeCount).toFixed(2) : '—';
                return (
                  <div key={round.id} className="round-row">
                    <span>{formatDate(round.roundStartedAt)}</span>
                    <span>{stats.holeCount}</span>
                    <span>{stats.totalPutts}</span>
                    <span>{avg}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentDetailPage;
